/*!
 * Colour and shape helpers of the pinball SigmaBot.
 *
 * Ported from sigmashake-vcs web/bots/shape-kit.ts (the point helpers, the clamp
 * helper, the HSL colour helper and the palette table).
 * Upstream project: ClaudeAnimationBase - https://github.com/JohnHeibel/ClaudeAnimationBase
 * MIT License. Copyright (c) 2026 John Heibel. The full MIT license text is in NOTICE.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */
// SPDX-License-Identifier: GPL-3.0-or-later
// Changes from upstream: the p5 drawing helpers, the boil helpers and the catalog tint helpers
// are not ported. The palette table has the same 18 names and the same numbers.

import type { SigmaBotPalette } from "./spec.ts";

export const TAU = Math.PI * 2;

/** Limit `x` to the range `a` to `b`. */
export const clamp = (x: number, a = 0, b = 1): number => Math.max(a, Math.min(b, x));

/** A 2D point in body units. */
export interface Point {
  x: number;
  y: number;
}

/** A colour as red, green, blue and alpha bytes (0 to 255). */
export type Rgba = readonly [number, number, number, number];

/** Points of an ellipse. */
export function ellPts(cx: number, cy: number, rx: number, ry: number, n = 18, rot = 0): Point[] {
  const out: Point[] = [];
  for (let i = 0; i < n; i++) {
    const a = rot + (i / n) * TAU;
    out.push({ x: cx + Math.cos(a) * rx, y: cy + Math.sin(a) * ry });
  }
  return out;
}

/** A rounded rectangle, corner radius `r`, `n` points for each corner. */
export function rrPts(x0: number, y0: number, x1: number, y1: number, r: number, n = 4): Point[] {
  const out: Point[] = [];
  const corner = (cx: number, cy: number, from: number): void => {
    for (let i = 0; i <= n; i++) {
      const a = from + (i / n) * (Math.PI / 2);
      out.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
    }
  };
  corner(x1 - r, y0 + r, -Math.PI / 2);
  corner(x1 - r, y1 - r, 0);
  corner(x0 + r, y1 - r, Math.PI / 2);
  corner(x0 + r, y0 + r, Math.PI);
  return out;
}

/** The left and right x of a closed polygon at height `y`. */
export function spanAt(pts: readonly Point[], y: number): [number, number] {
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i] as Point;
    const b = pts[(i + 1) % pts.length] as Point;
    if (a.y <= y !== b.y <= y) {
      const x = a.x + ((y - a.y) / (b.y - a.y)) * (b.x - a.x);
      lo = Math.min(lo, x);
      hi = Math.max(hi, x);
    }
  }
  return lo <= hi ? [lo, hi] : [0, 0];
}

/** HSL (degrees, percent, percent) to bytes. */
export function hsl(h: number, s: number, l: number, a = 255): Rgba {
  const hh = (((h % 360) + 360) % 360) / 360;
  const ss = clamp(s / 100);
  const ll = clamp(l / 100);
  const q = ll < 0.5 ? ll * (1 + ss) : ll + ss - ll * ss;
  const p = 2 * ll - q;
  const ch = (t0: number): number => {
    let t = t0;
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [
    Math.round(ch(hh + 1 / 3) * 255),
    Math.round(ch(hh) * 255),
    Math.round(ch(hh - 1 / 3) * 255),
    a,
  ];
}

/** A colour as one number for a three.js material (0xRRGGBB). */
export const hexOf = (c: Rgba): number => (c[0] << 16) | (c[1] << 8) | c[2];

/** Fixed colours of the face and the props. */
export const INK: Rgba = [27, 27, 36, 255];
export const CREAM: Rgba = [255, 247, 230, 255];
export const GOLD: Rgba = hsl(46, 95, 58);
export const PINK: Rgba = hsl(345, 85, 68);
export const MOUTH_DARK: Rgba = [74, 28, 44, 255];
export const STEEL: Rgba = hsl(215, 14, 72);
export const BROWN: Rgba = hsl(24, 45, 34);
export const WOOD: Rgba = hsl(30, 55, 48);
export const RED: Rgba = hsl(8, 92, 58);
export const SKY: Rgba = hsl(195, 85, 62);

/** Base colours of each palette: main hue, saturation, lightness, then the accent. */
const PALETTE_BASE: Record<
  SigmaBotPalette,
  { h: number; s: number; l: number; ah: number; as: number; al: number }
> = {
  ember: { h: 14, s: 85, l: 55, ah: 44, as: 95, al: 58 },
  ocean: { h: 200, s: 80, l: 52, ah: 165, as: 70, al: 55 },
  moss: { h: 100, s: 45, l: 40, ah: 60, as: 60, al: 62 },
  grape: { h: 280, s: 55, l: 50, ah: 320, as: 75, al: 68 },
  sun: { h: 48, s: 95, l: 58, ah: 18, as: 90, al: 52 },
  rose: { h: 340, s: 70, l: 72, ah: 300, as: 50, al: 80 },
  slate: { h: 215, s: 28, l: 46, ah: 190, as: 60, al: 62 },
  mint: { h: 160, s: 60, l: 70, ah: 200, as: 55, al: 45 },
  coral: { h: 6, s: 92, l: 70, ah: 175, as: 70, al: 45 },
  night: { h: 235, s: 40, l: 24, ah: 52, as: 95, al: 60 },
  peach: { h: 22, s: 95, l: 78, ah: 350, as: 70, al: 60 },
  sky: { h: 205, s: 90, l: 72, ah: 45, as: 95, al: 60 },
  lime: { h: 85, s: 70, l: 52, ah: 280, as: 55, al: 60 },
  snow: { h: 210, s: 20, l: 92, ah: 215, as: 60, al: 55 },
  gold: { h: 45, s: 90, l: 52, ah: 12, as: 80, al: 50 },
  cherry: { h: 352, s: 80, l: 45, ah: 40, as: 90, al: 62 },
  cocoa: { h: 25, s: 45, l: 30, ah: 40, as: 70, al: 58 },
  coal: { h: 220, s: 22, l: 14, ah: 45, as: 90, al: 58 },
};

/** The six colours of one palette. */
export interface Colors {
  main: Rgba;
  shade: Rgba;
  light: Rgba;
  accent: Rgba;
  accentDark: Rgba;
  accentLight: Rgba;
}

/** Build the colours of one palette. The hue offset is zero: the table does not shift hues. */
export function colorsFor(palette: SigmaBotPalette): Colors {
  const b = PALETTE_BASE[palette] ?? PALETTE_BASE.ember;
  return {
    main: hsl(b.h, b.s, b.l),
    shade: hsl(b.h, b.s, b.l - 14),
    light: hsl(b.h, b.s, Math.min(92, b.l + 16)),
    accent: hsl(b.ah, b.as, b.al),
    accentDark: hsl(b.ah, b.as, Math.max(12, b.al - 20)),
    accentLight: hsl(b.ah, b.as, Math.min(92, b.al + 18)),
  };
}
